import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { logger } from "@/lib/default-logger";
import { errorMessage } from "@/hooks/api/use-appointments";
import { toast } from "@/components/core/toaster";

import type { NumberVerification } from "./use-number-verification";

export type OnboardingGoal = "inbound" | "outbound" | "both";

export type HandlingMode = "messages" | "requests" | "bookings";

export interface OnboardingProfile {
	vertical: string | null;
	businessName: string;
	address: string | null;
	website: string | null;
	timezone: string;
}

export interface OnboardingReceptionist {
	handlingMode: HandlingMode;
	name: string | null;
	voiceId: string | null;
	greeting: string | null;
	transferNumber: string | null;
	afterHours: "message" | "book" | "voicemail";
	// Outcome names the owner is emailed about when a call ends in one.
	emailOutcomes: string[];
}

export interface OnboardingKnowledgeItem {
	id: string;
	title: string;
	content: string;
	source: string;
	enabled: boolean;
}

export interface OnboardingTestCall {
	// Verified in the last 24 hours: it can be called without another code.
	verifiedPhone: string | null;
	// A code was texted to it in the last 10 minutes.
	pendingPhone: string | null;
	codesLeft: number;
	callsLeft: number;
}

// Knowledge suggested from the website, waiting for review: nothing is in `knowledge` until the
// owner adds it.
export interface OnboardingWebsiteImport {
	status: "running" | "done" | "failed";
	url: string;
	startedAt: string;
	error: string | null;
	items?: { title: string; content: string }[];
}

// GET /onboarding
export interface OnboardingState {
	step: string;
	status: "in_progress" | "ready" | "live" | "pending_verification";
	numberVerification?: NumberVerification | null;
	canManageNumber?: boolean;
	// Services and hours live in the tenant schema; its API works only once this is true.
	tenantReady: boolean;
	// Whether the live receptionist answers calls; null until it goes live.
	receptionistActive: boolean | null;
	/** The receptionist agent whose script Settings edits; null until it is set up. */
	receptionistAgentId: string | null;
	// Why billing keeps the live receptionist from taking calls (absent from an older API).
	callsBlocked?: "payment_required" | "usage_limit" | "number_verification" | null;
	testCall: OnboardingTestCall;
	// Whether this person may change settings; absent from an older API.
	canUpdate?: boolean;
	goal: OnboardingGoal | null;
	profile: OnboardingProfile;
	receptionist: OnboardingReceptionist;
	knowledge: OnboardingKnowledgeItem[];
	websiteImport?: OnboardingWebsiteImport | null;
}

/**
 * Whether this person may change the business's setup (owner or admin). True until the state says
 * otherwise, so nothing is withheld while it loads or from an API that does not say.
 */
export function useCanUpdate(): boolean {
	return useOnboardingState().data?.canUpdate !== false;
}

// PUT /onboarding merges profile and receptionist key by key. The API rejects null for
// step, businessName, timezone, handlingMode and afterHours: leave them out instead.
export interface OnboardingUpdate {
	step?: string;
	goal?: OnboardingGoal | null;
	profile?: Partial<OnboardingProfile>;
	receptionist?: Partial<OnboardingReceptionist>;
	// A new item has no id yet: the server gives it one.
	knowledge?: (Omit<OnboardingKnowledgeItem, "id"> & { id?: string })[];
	// Only null: clears the website suggestions.
	websiteImport?: null;
}

export interface OnboardingTemplateService {
	name: string;
	durationMinutes: number;
	price?: number | null;
	recallIntervalDays?: number | null;
}

export interface OnboardingTemplate {
	vertical: string;
	title: string;
	goalSupport: string[];
	services: OnboardingTemplateService[];
	defaultGreeting: string;
}

export interface OnboardingVoice {
	id: string;
	name: string;
	description: string;
	previewUrl: string | null;
}

export const onboardingKeys = {
	all: ["onboarding"] as const,
	state: () => [...onboardingKeys.all, "state"] as const,
	templates: () => [...onboardingKeys.all, "templates"] as const,
	voices: () => [...onboardingKeys.all, "voices"] as const,
};

async function getOnboardingState(): Promise<OnboardingState> {
	const { data } = await apiClient.get("/onboarding");
	return data;
}

const STATE_STALE_TIME = 30 * 1000;
// The go-live worker runs once a minute.
const GO_LIVE_POLL_INTERVAL = 60_000;

// With pollUntilTenantReady, refetches every 2s while the tenant schema is still being prepared.
// Any observer refetches while the website is being read.
export function useOnboardingState(pollUntilTenantReady = false, enabled = true) {
	return useQuery({
		networkMode: "always",
		queryKey: onboardingKeys.state(),
		queryFn: getOnboardingState,
		enabled,
		retry: false,
		staleTime: STATE_STALE_TIME,
		refetchInterval: (query) => {
			const state = query.state.data;
			if ((pollUntilTenantReady && !state?.tenantReady) || state?.websiteImport?.status === "running") return 2000;
			// The number is active and the worker's next run takes the account live: nothing else
			// would tell an open page, since the number's own status has stopped changing.
			return state?.status === "pending_verification" && state.numberVerification?.status === "active"
				? GO_LIVE_POLL_INTERVAL
				: false;
		},
	});
}

// For redirects outside React Query hooks; null when the state cannot be read.
export async function fetchOnboardingState(queryClient: QueryClient): Promise<OnboardingState | null> {
	try {
		return await queryClient.fetchQuery({
			queryKey: onboardingKeys.state(),
			queryFn: getOnboardingState,
			retry: false,
			staleTime: STATE_STALE_TIME,
		});
	} catch {
		return null;
	}
}

// True only when setup is known to be unfinished: an unreadable state never locks anyone out.
export function isOnboardingIncomplete(state: OnboardingState | null | undefined): boolean {
	return Boolean(state && !hasDashboardAccess(state));
}

// Only confirmed server saves update the cache; sequential saves cannot overtake each other.
export function useUpdateOnboardingState() {
	const queryClient = useQueryClient();
	return useMutation({
		networkMode: "always",
		scope: { id: "save-onboarding" },
		mutationFn: async (patch: OnboardingUpdate): Promise<OnboardingState> => {
			const { data } = await apiClient.put("/onboarding", patch);
			return data;
		},
		// An in-flight poll must not land after the save and overwrite it.
		onMutate: () => queryClient.cancelQueries({ queryKey: onboardingKeys.state() }),
		onSuccess: (data) => {
			queryClient.setQueryData(onboardingKeys.state(), data);
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "Your changes could not be saved. Check your connection and try again."));
		},
	});
}

// POST /onboarding/import-website — the result arrives on the state, which polls meanwhile.
export function useImportWebsite() {
	const queryClient = useQueryClient();
	return useMutation({
		networkMode: "always",
		scope: { id: "save-onboarding" },
		mutationFn: async (body: { url: string }): Promise<OnboardingState> => {
			const { data } = await apiClient.post("/onboarding/import-website", body);
			return data;
		},
		// An in-flight poll must not land after the start and hide it.
		onMutate: () => queryClient.cancelQueries({ queryKey: onboardingKeys.state() }),
		onSuccess: (data) => queryClient.setQueryData(onboardingKeys.state(), data),
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "We couldn't start reading your website. Try again in a moment."));
		},
	});
}

export function useOnboardingTemplates() {
	return useQuery({
		networkMode: "always",
		queryKey: onboardingKeys.templates(),
		queryFn: async (): Promise<OnboardingTemplate[]> => {
			const { data } = await apiClient.get("/onboarding/templates");
			return data;
		},
		staleTime: 5 * 60 * 1000,
	});
}

export function useOnboardingVoices() {
	return useQuery({
		networkMode: "always",
		queryKey: onboardingKeys.voices(),
		queryFn: async (): Promise<OnboardingVoice[]> => {
			const { data } = await apiClient.get("/onboarding/voices");
			return data;
		},
		staleTime: 5 * 60 * 1000,
	});
}

// POST /onboarding/verify-test-phone — texts a code to the mobile, or checks the code.
export function useVerifyTestPhone() {
	const queryClient = useQueryClient();
	return useMutation({
		networkMode: "always",
		mutationFn: async (body: { phone: string; code?: string }): Promise<{ verified: boolean }> => {
			const { data } = await apiClient.post("/onboarding/verify-test-phone", body);
			return data;
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "We couldn't verify that number. Try again in a moment."));
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey: onboardingKeys.state() }),
	});
}

// POST /onboarding/test-call — the live receptionist calls the verified mobile.
export function useTestCall() {
	const queryClient = useQueryClient();
	return useMutation({
		networkMode: "always",
		mutationFn: async (body: { phone: string }): Promise<{ callId: string }> => {
			const { data } = await apiClient.post("/onboarding/test-call", body);
			return data;
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "We couldn't start the call. Try again in a moment."));
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey: onboardingKeys.state() }),
	});
}

// PUT /onboarding/receptionist-active — pauses or resumes the live receptionist.
export function useSetReceptionistActive() {
	const queryClient = useQueryClient();
	return useMutation({
		networkMode: "always",
		scope: { id: "save-onboarding" },
		mutationFn: async (active: boolean): Promise<OnboardingState> => {
			const { data } = await apiClient.put("/onboarding/receptionist-active", { active });
			return data;
		},
		onMutate: () => queryClient.cancelQueries({ queryKey: onboardingKeys.state() }),
		onSuccess: (data) => {
			queryClient.setQueryData(onboardingKeys.state(), data);
			toast.success(data.receptionistActive ? "Your receptionist is answering calls" : "Your receptionist is paused");
		},
		onError: (error: any) => {
			logger.error(error);
			void queryClient.invalidateQueries({ queryKey: onboardingKeys.state() });
			toast.error(errorMessage(error, "We couldn't change that. Try again in a moment."));
		},
	});
}

// POST /onboarding/complete: provisions the receptionist and switches it on.
export function useCompleteOnboarding() {
	const queryClient = useQueryClient();
	return useMutation({
		networkMode: "always",
		scope: { id: "save-onboarding" },
		mutationFn: async (): Promise<OnboardingState> => {
			const { data } = await apiClient.post("/onboarding/complete");
			return data;
		},
		onMutate: () => queryClient.cancelQueries({ queryKey: onboardingKeys.state() }),
		onSuccess: (data) => {
			queryClient.setQueryData(onboardingKeys.state(), data);
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(errorMessage(error, "Your receptionist could not be switched on. Try again in a moment."));
		},
	});
}

// Paying for the plan opens the dashboard; calls may still wait for the number's verification.
export function hasDashboardAccess(state?: Pick<OnboardingState, "status"> | null): boolean {
	return state?.status === "live" || state?.status === "pending_verification";
}
